import { gql } from '@apollo/client';

export const GET_TEMPLATE = gql`
  query GetTemplate($identifier: String!) {
    getTemplateByIdentifier(identifier: $identifier) {
      id
      identifier
      templateId {
        id
        milestoneIds {
          id
          name
          sequence
        }
      }
    }
  }
`;

export const GET_EMPLOYEES_AND_DEAL_SELECTS = gql`
  query EmployeesAndDealSelects {
    hrEmployees {
      edges {
        node {
          id
          name
          workEmail
          email
          userId {
            id
            email
          }
          departmentId {
            name
          }
        }
      }
    }
    statusSelections: getFieldSelections(
      model: "opstream.deal.opportunity"
      fieldName: "status"
    ) {
      id
      name
      value
    }
    dealSourceTypeSelections: getFieldSelections(
      model: "opstream.deal.opportunity"
      fieldName: "deal_source_type"
    ) {
      id
      name
      value
    }
    smcIndustrySelections: getFieldSelections(
      model: "opstream.deal.opportunity"
      fieldName: "smc_industry"
    ) {
      name
      value
    }
    killReasonSelections: getFieldSelections(
      model: "opstream.deal.opportunity"
      fieldName: "kill_reason"
    ) {
      id
      name
      value
    }
    useOfProceedsSelections: getFieldSelections(
      model: "opstream.liquidity.allocation"
      fieldName: "use_of_proceeds"
    ) {
      id
      name
      value
    }
  }
`;

export const GET_DEAL_OPPORTUNITIES = gql`
  query DealOpportunities(
    $first: Int
    $offset: Int
    $after: String
    $last: Int
    $before: String
    $deal_team: [ID]
    $name: String
    $status: String
    $only_active_opportunities: Boolean
    $phase: [String]
    $stage: [String]
    $source_ids: [ID]
    $order: String
  ) {
    opportunities(
      first: $first
      offset: $offset
      after: $after
      last: $last
      before: $before
      dealTeamIds: $deal_team
      name: $name
      status: $status
      onlyActiveOpportunities: $only_active_opportunities
      phase: $phase
      stage: $stage
      sourceIds: $source_ids
      order: $order
    ) {
      totalCount
      edges {
        node {
          id
          totalProposedEquityInvestment
          totalProposedDebtInvestment
          name
          projectName
          targetEntityName
          targetEntityId {
            ... on AccountNode {
              id
              name
            }
          }
          createDate
          smcSource {
            id
            name
          }
          status
          stage
          killReason
          killedDealNotes
          mdTeamId {
            id
            name
          }
          md2TeamId {
            id
            name
          }
          juniorTeamIds {
            id
            name
          }
          midTeamIds {
            id
            name
          }
          dueDiligenceProjectId {
            nextMilestoneName
          }
          smcIndustry
          readableSmcIndustry
          smPrelHold
          referencePoint
          bestEstimateDate
          ioiDueDate
          ltmEbitda
          ltmRevenue
          dealSource {
            name
          }
        }
        cursor
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
  }
`;

export const GET_MY_DEALS = gql`
  query MyDeals {
    myDeals {
      edges {
        node {
          id
          totalProposedEquityInvestment
          totalProposedDebtInvestment
          name
          projectName
          targetEntityName
          targetEntityId {
            ... on AccountNode {
              id
              name
            }
          }
          createDate
          smcSource {
            id
            name
          }
          status
          stage
          killReason
          killedDealNotes
          mdTeamId {
            id
            name
          }
          md2TeamId {
            id
            name
          }
          juniorTeamIds {
            id
            name
          }
          midTeamIds {
            id
            name
          }
          dueDiligenceProjectId {
            nextMilestoneName
          }
          smcIndustry
          readableSmcIndustry
          smPrelHold
          referencePoint
          bestEstimateDate
          ioiDueDate
          ltmEbitda
          ltmRevenue
          dealSource {
            id
            name
          }
        }
        cursor
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
    }
  }
`;

export const CREATE_OPPORTUNITY = gql`
  mutation CreateOpportunity(
    $projectName: String!
    $targetEntityId: String
    $smcIndustry: String
    $smcSource: ID
    $dealSourceId: ID
    $dealSourceType: String
    $referencePoint: String
    $ioiDueDate: Date
    $totalProposedDebtInvestment: Float
    $smProjectedDebtInvestment: Float
    $totalProposedEquityInvestment: Float
    $smProjectedEquityInvestment: Float
    $status: String
    $killReason: String
    $details: String
    $ltmEbitda: Float
    $emailBody: String
    $killedDealNotes: String
    $best_estimate_date: Date
    $earliest_date: Date
    $latest_date: Date
    $sm_close_date_flexibility: Boolean
    $probabilty_of_close: Float
    $smaller_enterprise: String
  ) {
    createOpportunity(
      values: {
        smcSource: $smcSource
        projectName: $projectName
        targetEntityId: $targetEntityId
        smcIndustry: $smcIndustry
        dealSource: $dealSourceId
        dealSourceType: $dealSourceType
        referencePoint: $referencePoint
        ioiDueDate: $ioiDueDate
        ltmEbitda: $ltmEbitda
        totalProposedDebtInvestment: $totalProposedDebtInvestment
        smProjectedDebtInvestment: $smProjectedDebtInvestment
        totalProposedEquityInvestment: $totalProposedEquityInvestment
        smProjectedEquityInvestment: $smProjectedEquityInvestment
        status: $status
        killReason: $killReason
        details: $details
        emailBody: $emailBody
        killedDealNotes: $killedDealNotes
        bestEstimateDate: $best_estimate_date
        earliestDate: $earliest_date
        latestDate: $latest_date
        smcCloseDateFlexibility: $sm_close_date_flexibility
        probabilityOfClose: $probabilty_of_close
        smallerEnterprise: $smaller_enterprise
      }
    ) {
      ok
      opportunity {
        id
      }
    }
  }
`;

export const GET_ACCOUNTS = gql`
  query SearchWithSelectedAccounts($name: [String]) {
    searchAccounts(name: $name, limit: 100) {
      id
      name
      email
      relationshipType
      isCompany
      city
    }
  }
`;

export const GET_ALL_FUNDS = gql`
  query GetAllFunds(
    $status: String
    $isSmaIma: Boolean
    $isActive: Boolean
    $fundTypeCodes: [String]
  ) {
    funds(
      status: $status
      isSmaIma: $isSmaIma
      isActive: $isActive
      fundTypeCodes: $fundTypeCodes
    ) {
      edges {
        node {
          id
          canAcceptNewPlatforms
          name
          balance
          buyingPower
          concentrationLimit
          includeIneligibleFundsInAllocation
          fundTypes {
            id
            name
            code
          }
          status
          classification
          isSmaIma
        }
      }
    }
  }
`;

export const GET_ALLOCATION_DETAILS = gql`
  query GetAllocationDetails($id: ID!) {
    node(id: $id) {
      ... on LiquidityAllocationNode {
        id
        allocationAmount
        debtAllocationAmount
        equityAllocationAmount
        allocationType
        useOfProceeds
        numberOfFunds
        totalAllocated
        isFulfilled
        allocationLines {
          id
          fundId
          allocatedAmount
          debtAllocatedAmount
          equityAllocatedAmount
          fairShareAmount
          fairShareDebtAmount
          fairShareEquityAmount
          concentrationLimitPct
          concentrationLimitAmount
          isExcluded
          isOverLimit
          excessAmount
          allocationPercentage
          fund {
            id
            name
            balance
            buyingPower
          }
        }
      }
    }
  }
`;

export const CREATE_ALLOCATION = gql`
  mutation CreateAllocation($values: AllocationInput!) {
    createAllocation(values: $values) {
      ok
      errors
      allocation {
        id
        opportunityId
        allocationAmount
        debtAllocationAmount
        equityAllocationAmount
        allocationType
        status
        useOfProceeds
        numberOfFunds
        totalAllocated
        version
        isFulfilled
        allocationLines {
          id
          fundId
          allocatedAmount
          debtAllocatedAmount
          equityAllocatedAmount
          fairShareAmount
          fairShareDebtAmount
          fairShareEquityAmount
          concentrationLimitPct
          concentrationLimitAmount
          isExcluded
          isOverLimit
          excessAmount
          allocationPercentage
          fund {
            id
            name
            balance
            buyingPower
          }
        }
      }
    }
  }
`;

export const SUBMIT_FOR_REVIEW = gql`
  mutation SubmitForReview($allocationId: ID!) {
    submitForReview(allocationId: $allocationId) {
      ok
      allocation {
        id
        status
      }
      errors
    }
  }
`;

export const UPDATE_OPPORTUNITY = gql`
  mutation UpdateOpportunityStatus(
    $id: ID!
    $status: String!
    $killReason: String
    $killedDealNotes: String
  ) {
    updateOpportunity(
      id: $id
      values: {
        status: $status
        killReason: $killReason
        killedDealNotes: $killedDealNotes
      }
    ) {
      ok
    }
  }
`;

export const GENERATE_RSM_REPORT = gql`
  mutation GenerateRSM($id: ID!) {
    generateRsmReport(opportunityId: $id) {
      ok
      data
    }
  }
`;

export const GENERATE_ICB_REPORT = gql`
  mutation GenerateIcbReport($id: ID!) {
    generateIcbReport(opportunityId: $id) {
      ok
      data
    }
  }
`;

export const GENERATE_ICM_REPORT = gql`
  mutation GenerateICMReport($id: ID!) {
    generateIcmReport(opportunityId: $id) {
      ok
      data
    }
  }
`;
